import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { FaPills, FaSearchDollar, FaListUl, FaPrescriptionBottle } from "react-icons/fa";

const FAQItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left px-5 py-4 font-semibold flex justify-between">
        <span>{q}</span>
        <span className={`transform transition ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      {open && <div id={`faq-${i}`} className="px-5 pb-5 text-gray-600">{a}</div>}
    </div>
  );
};

export default function PartDPlans() {
  return (
    <>
      <Helmet>
        <title>Medicare Part D Drug Plans | Your Choice Associates</title>
        <meta
          name="description"
          content="Lower prescription costs with Medicare Part D. We compare formularies, pharmacies, and premiums to find the right plan."
        />
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#145ea8] via-[#0f4f8e] to-[#002b5c]" />
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative max-w-7xl mx-auto px-6 md:px-8 pt-20 pb-16 text-white"
        >
          <span className="inline-block text-xs font-bold tracking-widest uppercase bg-white/10 backdrop-blur px-3 py-1 rounded-full">
            Part D Prescription Coverage
          </span>
          <h1 className="text-3xl md:text-5xl font-extrabold mt-4">Pay less for your medications</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            We compare plans based on your actual drug list, preferred pharmacies, and monthly budget.
          </p>
        </motion.div>
      </section>

      {/* Highlights */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <FaSearchDollar />, title: "Cost comparison", desc: "Premium + copays + deductible for a real total cost view." },
            { icon: <FaListUl />, title: "Formulary checks", desc: "We verify each medication’s tier and coverage rules." },
            { icon: <FaPrescriptionBottle />, title: "Pharmacy matching", desc: "Confirm preferred vs. standard pharmacy pricing." },
            { icon: <FaPills />, title: "Gap coverage", desc: "Understand how the Coverage Gap (donut hole) affects you." },
          ].map((c, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
            >
              <div className="text-2xl">{c.icon}</div>
              <h3 className="mt-4 font-bold text-lg">{c.title}</h3>
              <p className="mt-2 text-gray-600">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How Part D works */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">How Part D works</h2>
          <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li><strong>Premium</strong> you pay monthly for the plan.</li>
              <li><strong>Deductible</strong> you may pay before copays begin (some drugs bypass it).</li>
              <li><strong>Copays/coinsurance</strong> vary by drug tier and pharmacy type.</li>
              <li><strong>Coverage Gap</strong> (donut hole) where share of cost changes temporarily.</li>
              <li><strong>Catastrophic</strong> phase lowers your cost later in the year.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">Part D FAQs</h2>
        <div className="space-y-3">
          {[
            { q: "Do I need Part D if I don’t take meds now?", a: "You can delay, but late‑enrollment penalties may apply later. We’ll check if creditable coverage from an employer plan applies." },
            { q: "Are my medications covered?", a: "Each plan has a formulary. We’ll enter your drug list and confirm tier, quantity limits, and any prior authorization." },
            { q: "When can I change plans?", a: "Generally during the Annual Enrollment Period (Oct 15–Dec 7) or if you qualify for a Special Enrollment Period." },
          ].map((f, i) => <FAQItem key={i} i={i} {...f} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-[#002b5c] text-white rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-lg">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold">Get a custom drug cost report</h3>
              <p className="mt-2 text-white/90">We’ll run the numbers and recommend the most cost‑effective plan.</p>
            </div>
            <a href="/start" className="inline-flex items-center font-semibold bg-white text-[#002b5c] px-5 py-3 rounded-xl hover:opacity-90 transition">
              Start Free Review
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
